import { FunctionComponent, RefObject } from 'preact';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { useStyles } from '../../../common/hooks/useStyles';
import { getSeekBarContainerStyleObject } from './seekBarContainer.style';
import { SeekBarBuffer } from './seekBarBuffer/seekBarBuffer';
import { SeekBarProgress } from './seekBarProgress/seekBarProgress';
import { playerDataSelector } from '../../../common/store/selectors/playerData.selector';
import { debounce, getTimePresentation, isDesktopMode, isEmpty, isPassiveListenersSupported } from '../../../common/utils/utils';
import { adsDataSelector } from '../../../common/store/selectors/adsData.selector';
import { css } from '../../../common/styles/styles.utils';
import { getSeekBarId, getSeekBarTooltipId } from '../htmlElements/htmlElementsIdGetter';
import { resumeVideo, setPosition, setUserInteraction } from '../store/controlsPlugin.actions';
import {brandingDataSelector} from '../../../common/store/selectors/brandingData.selector';

const ARIA_LABEL = 'Seekbar';

interface SeekBarComponentVariablesProps {
  clickToPlayMode: boolean;
}

export const SeekBarContainer: FunctionComponent<SeekBarComponentVariablesProps> = props => {
  const { clickToPlayMode } = props;

  const dispatch = useDispatch();
  const loadingPlayer = useSelector(playerDataSelector.loadingPlayer);
  const currentVideoTime = useSelector(playerDataSelector.currentVideoTime);
  const currentVideoDuration = useSelector(playerDataSelector.currentVideoDuration);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);
  const isVastAd = useSelector(adsDataSelector.isVastAd);
  const adBreak = useSelector(adsDataSelector.adBreak);
  const playerSize = useSelector(playerDataSelector.playerSize);
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqId);
  const playerMode = useSelector(playerDataSelector.playerMode);
  const controlsDisplay = useSelector(brandingDataSelector.controlsDisplay);

  const showAdControls = isVastAd && adBreak;
  const desktopMode = isDesktopMode(playerMode);
  const viewDisplay = controlsDisplay === 'viewDisplay';

  const seekBarContainerRef: RefObject<HTMLDivElement> = useRef();
  const toolTipSeekBarRef: RefObject<HTMLDivElement> = useRef();

  const [isMouseDown, setMouseDown] = useState(false);
  const [isPositionChanged, setPositionChanged] = useState(false);
  const [videoStatusBeforeDragging, setVideoStatusBeforeDragging] = useState(videoTagStatus);

  const styles = useStyles(getSeekBarContainerStyleObject, [playerSize, desktopMode, showAdControls, clickToPlayMode, viewDisplay]);
  const { seekBarContainerStyle, seekBarStyle, seekBarAndToolTipContainerStyle, toolTipStyle } = styles;

  const getOffsetX = (mouseEvent: Event, isTouchEvent = false): number => {
    if (isTouchEvent) {
      return (mouseEvent as TouchEvent).changedTouches[0].clientX - seekBarContainerRef.current.getBoundingClientRect().left;
    }
    return (mouseEvent as MouseEvent).clientX - seekBarContainerRef.current.getBoundingClientRect().left;
  };

  const onSeek = useCallback((mouseEvent: Event, isTouchEvent = false): void => {
    const percentOfSeekBar = getOffsetX(mouseEvent, isTouchEvent) / seekBarContainerRef.current.clientWidth;
    const newPosition = percentOfSeekBar * currentVideoDuration;
    setPositionChanged(true);
    dispatch(setPosition(newPosition));
    dispatch(setUserInteraction('seek'));
  }, [currentVideoDuration, dispatch]);

  const setTooltipPosition = (percent: number, offsetLeft: number): void => {
    const threshold = 25;
    if (offsetLeft < threshold) {
      toolTipSeekBarRef.current.style.left = `${threshold}px`;
    } else if (offsetLeft > seekBarContainerRef.current.clientWidth - threshold) {
      toolTipSeekBarRef.current.style.left = `${seekBarContainerRef.current.clientWidth - threshold}px`;
    } else {
      toolTipSeekBarRef.current.style.left = `${percent * 100}%`;
      toolTipSeekBarRef.current.style.transform = 'translateX(-50%)';
    }
    toolTipSeekBarRef.current.style.opacity = '1';
  };

  const showTimeTooltip = useCallback((mouseEvent: MouseEvent): void => {
    const offsetX = getOffsetX(mouseEvent);
    const offsetLeft = offsetX > 0 ? offsetX : 0;
    const percentOfSeekBar = offsetLeft / seekBarContainerRef.current.clientWidth;
    const newPosition = percentOfSeekBar * currentVideoDuration;
    setTooltipPosition(percentOfSeekBar, offsetLeft);
    toolTipSeekBarRef.current.innerText = getTimePresentation(Math.floor(newPosition));
  }, [currentVideoDuration]);

  const onMouseLeave = (): void => {
    if (!isEmpty(toolTipSeekBarRef.current)) {
      toolTipSeekBarRef.current.style.opacity = '0';
    }
  };

  const onMouseMove = (mouseEvent: MouseEvent): void => {
    if (desktopMode) {
      debounce(showTimeTooltip(mouseEvent), 80);
    }
  };

  const draggableSeek = useCallback((mouseEvent: MouseEvent | TouchEvent): void => {
    debounce(onSeek(mouseEvent), 80);
  }, [onSeek]);

  const onMouseMoveOrTouchMove = useCallback((mouseEvent: MouseEvent | TouchEvent): void => {
    draggableSeek(mouseEvent);
  }, [draggableSeek]);

  const onMouseUpOrTouchEnd = useCallback((): void => {
    setPositionChanged(false);
    setMouseDown(false);
    if (videoStatusBeforeDragging === 'playing') {
      dispatch(resumeVideo());
    }
    document.removeEventListener('mousemove', onMouseMoveOrTouchMove);
    document.removeEventListener('touchmove', onMouseMoveOrTouchMove);
    document.removeEventListener('mouseup', onMouseUpOrTouchEnd);
    document.removeEventListener('touchend', onMouseUpOrTouchEnd);
  }, [onMouseMoveOrTouchMove, dispatch, videoStatusBeforeDragging]);

  const onStartSeek = (event: Event, isTouchEvent: boolean): void => {
    setVideoStatusBeforeDragging(videoTagStatus);
    setMouseDown(true);
    onSeek(event, isTouchEvent);
  };

  const onMouseDown = (mouseEvent: MouseEvent): void => {
    onStartSeek(mouseEvent, false);
  };

  const onTouchStart = (touchEvent: TouchEvent): void => {
    onStartSeek(touchEvent, true);
  };

  useEffect(() => {
    if (isMouseDown) {
      const supported = isPassiveListenersSupported();
      document.addEventListener('mousemove', onMouseMoveOrTouchMove, supported ? { passive: true } : false);
      document.addEventListener('touchmove', onMouseMoveOrTouchMove, supported ? { passive: true } : false);
      document.addEventListener('mouseup', onMouseUpOrTouchEnd, supported ? { passive: true } : false);
      document.addEventListener('touchend', onMouseUpOrTouchEnd, supported ? { passive: true } : false);
    }
    return (): void => {
      document.removeEventListener('mousemove', onMouseMoveOrTouchMove);
      document.removeEventListener('touchmove', onMouseMoveOrTouchMove);
      document.removeEventListener('mouseup', onMouseUpOrTouchEnd);
      document.removeEventListener('touchend', onMouseUpOrTouchEnd);
    };
  }, [isMouseDown, onMouseMoveOrTouchMove, onMouseUpOrTouchEnd]);

  const additionalSeekBarProps = !showAdControls ? {
    onMouseDown,
    onTouchStart,
    onMouseMove,
    onMouseLeave,
  } : {};

  const getNewPosition = (keyType: string): number => {
    if (keyType === 'ArrowRight') {
      return currentVideoDuration - currentVideoTime > 5 ? currentVideoTime + 5 : currentVideoDuration;
    }
    if (keyType === 'ArrowLeft') {
      return currentVideoTime > 5 ? currentVideoTime - 5 : 0;
    }
    return currentVideoTime;
  };

  const onSeekBarKeyDown = (event: any): void => {
    if (!loadingPlayer && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      event.preventDefault();
      const newPosition = getNewPosition(event.key);
      setPositionChanged(true);
      dispatch(setPosition(newPosition));
      dispatch(setUserInteraction('seek'));
      if (videoTagStatus === 'playing') {
        dispatch(resumeVideo());
      }
    }
  };

  const SeekBar: FunctionComponent = () => (
    <div
      style={css(seekBarContainerStyle)}
      ref={seekBarContainerRef}
      id={getSeekBarId(playerUniqueId)}
      {...additionalSeekBarProps}
    >
      <div style={css(seekBarStyle)}>
        <SeekBarBuffer />
        <SeekBarProgress isPositionChanged={isPositionChanged} />
      </div>
    </div>

  );

  return (
    desktopMode
      ? (
        <div style={css(seekBarAndToolTipContainerStyle)}>
          <div
            id={getSeekBarTooltipId(playerUniqueId)}
            style={toolTipStyle}
            ref={toolTipSeekBarRef}
          />
          <div onKeyDown={onSeekBarKeyDown} tabIndex={0} aria-label={ARIA_LABEL}>
            <SeekBar />
          </div>
        </div>
      )
      : <SeekBar />
  );
};
